/**
 * pushStaff — alta/baja de las notificaciones push del OS en este dispositivo (PWA).
 * La clave VAPID y la suscripción viajan por /api/push/*; el aviso lo muestra el service worker (sw-os.js).
 */
import { api } from "../api/client.js";

const aBytes = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
};

export const pushSoportado = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** "activo" | "inactivo" | "bloqueado" | "no-soportado" */
export async function estadoPush() {
  if (!pushSoportado()) return "no-soportado";
  if (Notification.permission === "denied") return "bloqueado";
  try {
    const reg = await navigator.serviceWorker.ready;
    return (await reg.pushManager.getSubscription()) ? "activo" : "inactivo";
  } catch { return "inactivo"; }
}

export async function activarPush() {
  if (!pushSoportado()) throw new Error("Este navegador no soporta notificaciones push.");
  const permiso = await Notification.requestPermission();
  if (permiso !== "granted") throw new Error("No diste permiso para notificaciones.");
  const { key, activo } = await api.request("GET", "/api/push/public-key");
  if (!key || activo === false) throw new Error("Las notificaciones push no están disponibles ahora.");
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: aBytes(key) });
  await api.request("POST", "/api/push/subscribe", { subscription: sub.toJSON() });
  return true;
}

export async function desactivarPush() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.request("POST", "/api/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return true;
}

/** Si el navegador tiene la suscripción pero el servidor no (se dio de baja tras un rechazo), la vuelve a registrar. */
export async function sincronizarSuscripcion() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  await api.request("POST", "/api/push/subscribe", { subscription: sub.toJSON() });
  return true;
}

async function endpointActual() {
  const reg = await navigator.serviceWorker.ready;
  return (await reg.pushManager.getSubscription())?.endpoint || null;
}

/** preferencias de ESTE dispositivo: { prefs: { categorias, sonido }, categorias: { clave: {titulo, desc} } } */
export async function leerPrefsPush() {
  const endpoint = await endpointActual();
  if (!endpoint) return null;
  return api.request("GET", `/api/push/prefs?endpoint=${encodeURIComponent(endpoint)}`);
}

export async function guardarPrefsPush(prefs) {
  const endpoint = await endpointActual();
  if (!endpoint) throw new Error("Este dispositivo no está suscrito.");
  return api.request("POST", "/api/push/prefs", { endpoint, prefs });
}

/** Prueba de un tipo de aviso. Devuelve el resultado real del envío: { enviados, fallos:[{codigo,detalle}] } */
export const probarPush = (categoria) => api.request("POST", "/api/push/test", { categoria });
